import * as React from "react";
import { Area, AreaChart, CartesianGrid, XAxis, LabelList, Line } from "recharts";
import { ChartConfig, ChartContainer, ChartTooltip } from "@/components/shadcn/chart";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/shadcn/tooltip";
import { Checkbox } from "@/components/shadcn/checkbox";
import { type PlayerData } from "@/api/player";
import dayjs from "dayjs";
import { dateFormat } from "@/lib/utils";
import { Loader } from "../../common/loader";
import { calculateTrendLine, getMatchInfo, resolveKpiColor, TrendLabel, type KpiOption } from "./chartUtils";
import { useIsMobile } from "@/hooks/use-mobile";

interface ChartDataPoint {
    date: string;
    matchNumber: number;
    opponent: string;
    result: "W" | "D" | "L";
    score: string;
    matchDate: string;
    homeTeam: string;
    awayTeam: string;
    [key: string]: string | number;
}

const FIELD_KPI_OPTIONS: KpiOption[] = [
    { value: "totalPoints", label: "Total", fullLabel: "Total Points", color: "blue" },
    { value: "defensivePoints", label: "Defensive", fullLabel: "Defensive Performance", color: "green" },
    { value: "offensivePoints", label: "Offensive", fullLabel: "Offensive Performance", color: "red" },
    { value: "teamPlayPoints", label: "Team Play", fullLabel: "Team Play Statistics", color: "yellow" },
    { value: "effortPoints", label: "Effort", fullLabel: "Effort Metrics", color: "purple" },
    { value: "bonusPoints", label: "Bonus", fullLabel: "Bonus Points", color: "orange" },
    { value: "timePoints", label: "Time On Ice", fullLabel: "Time On Ice (Minutes)", color: "cyan" },
];

function mapFieldPlayerData(playerData: PlayerData): ChartDataPoint[] {
    if (!playerData.kpis || playerData.kpis.length === 0) return [];
    const kpis = [...playerData.kpis].sort((a, b) => new Date(a.matchDate).getTime() - new Date(b.matchDate).getTime());
    return kpis.map((kpi, index) => {
        const { opponent, result, score } = getMatchInfo(
            kpi.homeTeam,
            kpi.awayTeam,
            kpi.homeGoals,
            kpi.awayGoals,
            playerData.teamId,
            kpi.homeTeamId,
            kpi.awayTeamId
        );
        return {
            date: dayjs(kpi.matchDate).format(dateFormat),
            matchNumber: index + 1,
            totalPoints: kpi.totalPoints,
            defensivePoints: kpi.defensiveData.points,
            offensivePoints: kpi.offensiveData.points,
            teamPlayPoints: kpi.teamPlayData.points,
            effortPoints: kpi.effortData.points,
            bonusPoints: kpi.bonusData.points,
            timePoints: kpi.timeData.timeOnIce.total / 60,
            opponent,
            result,
            score,
            matchDate: kpi.matchDate,
            homeTeam: kpi.homeTeam,
            awayTeam: kpi.awayTeam,
            shotsAgainstHome: kpi.shotsAgainstHome,
            shotsAgainstAway: kpi.shotsAgainstAway,
            role: kpi.role,
        };
    });
}

const GK_KPI_OPTIONS: KpiOption[] = [
    { value: "totalPoints", label: "Total", fullLabel: "Total Points", color: "blue" },
    { value: "gsaxPoints", label: "GSAx", fullLabel: "Goals Saved Above Expected", color: "orange" },
    { value: "highDangerPoints", label: "Hi-Dan", fullLabel: "High Danger Shots", color: "red" },
    { value: "lowDangerPoints", label: "Lo-Dan", fullLabel: "Low Danger Shots", color: "green" },
    { value: "lateralPassPoints", label: "Lat.Pass", fullLabel: "Lateral Pass Shots", color: "yellow" },
    { value: "screenedPoints", label: "Screened", fullLabel: "Screened Shots", color: "purple" },
];

function mapGoalkeeperData(playerData: PlayerData): ChartDataPoint[] {
    if (!playerData.goalkeeperKpis || playerData.goalkeeperKpis.length === 0) return [];
    const kpis = [...playerData.goalkeeperKpis].sort(
        (a, b) => new Date(a.matchDate).getTime() - new Date(b.matchDate).getTime()
    );
    return kpis.map((kpi, index) => {
        const { opponent, result, score } = getMatchInfo(
            kpi.homeTeam,
            kpi.awayTeam,
            kpi.homeGoals,
            kpi.awayGoals,
            playerData.teamId,
            kpi.homeTeamId,
            kpi.awayTeamId
        );
        return {
            date: dayjs(kpi.matchDate).format(dateFormat),
            matchNumber: index + 1,
            totalPoints: kpi.totalPoints,
            gsaxPoints: kpi.pointsGSAx.pointsGSAx || 0,
            highDangerPoints: kpi.zone1.pointsHighDangerShots || 0,
            lowDangerPoints: kpi.zone4.pointsLowDangerShots || 0,
            lateralPassPoints: kpi.lateralPass.pointsLateralPassShots || 0,
            screenedPoints: kpi.screened.pointsScreenedShots || 0,
            opponent,
            result,
            score,
            matchDate: kpi.matchDate,
            homeTeam: kpi.homeTeam,
            awayTeam: kpi.awayTeam,
        };
    });
}

function buildChartConfig(options: KpiOption[]): ChartConfig {
    const config: ChartConfig = {};
    for (const o of options) {
        config[o.value] = { label: o.fullLabel, color: o.color };
    }
    return config;
}

interface PlayerKPIChartProps {
    playerData?: PlayerData;
    selectedKPI?: string;
    onKPIChange?: (kpi: string) => void;
    isLoading?: boolean;
    onTitleChange?: (title: { main: string; sub: string; subColor?: string }) => void;
}

export function PlayerKPIChart({
    playerData,
    selectedKPI = "totalPoints",
    onKPIChange,
    isLoading,
    onTitleChange,
}: PlayerKPIChartProps) {
    const [showTrendLine, setShowTrendLine] = React.useState(true);
    const isMobile = useIsMobile();

    const isGoalkeeper = playerData?.role?.includes("GK") ?? false;
    const kpiOptions = isGoalkeeper ? GK_KPI_OPTIONS : FIELD_KPI_OPTIONS;
    const chartConfig = React.useMemo(() => buildChartConfig(kpiOptions), [kpiOptions]);
    const chartTitle = isGoalkeeper ? "Goalkeeper Performance Over Time" : "Player Performance Over Time";

    const filteredData = React.useMemo((): ChartDataPoint[] => {
        if (!playerData) return [];
        const data = isGoalkeeper ? mapGoalkeeperData(playerData) : mapFieldPlayerData(playerData);
        if (data.length === 0) return [];
        return calculateTrendLine(data, selectedKPI);
    }, [playerData, selectedKPI, isGoalkeeper]);

    const selectedKPIConfig = kpiOptions.find((option) => option.value === selectedKPI);

    const minWidthPerPoint = React.useMemo(() => {
        if (!isMobile || filteredData.length === 0) return 20;
        const maxLen = filteredData.reduce((max, d) => {
            const v = d[selectedKPI];
            if (typeof v !== "number") return max;
            const str = v % 1 === 0 ? v.toString() : v.toFixed(1);
            return Math.max(max, str.length);
        }, 2);
        return Math.max(20, maxLen * 9);
    }, [filteredData, selectedKPI, isMobile]);

    React.useEffect(() => {
        onTitleChange?.({
            main: chartTitle,
            sub: selectedKPIConfig?.fullLabel || "Total Points",
            subColor: resolveKpiColor(selectedKPIConfig?.color),
        });
    }, [chartTitle, selectedKPIConfig, onTitleChange]);

    if (isLoading && !playerData) {
        return (
            <div className="flex items-center justify-center h-[400px]">
                <Loader message="Loading KPI chart..." className="text-sm" />
            </div>
        );
    }
    if (!playerData || filteredData.length === 0) {
        return (
            <div className="flex items-center justify-center h-[400px]">
                <p className="text-gray-400">No KPI data available</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full animate-delay-500 p-2 md:p-4">
            <div className="flex flex-wrap gap-2 mb-1 shrink-0 md:flex-col md:items-end">
                <nav
                    id="onboarding-player-kpi-selector"
                    className="rounded-md p-1 bg-surface-container-low basis-full md:basis-auto"
                >
                    <div className="grid grid-cols-3 gap-1 md:flex md:items-center md:space-x-2 md:gap-0">
                        {kpiOptions.map((option) => (
                            <Tooltip key={option.value}>
                                <TooltipTrigger asChild>
                                    <button
                                        onClick={() => onKPIChange?.(option.value)}
                                        className={`nav-tab ${
                                            selectedKPI === option.value ? "text-white shadow-sm" : "nav-tab-inactive"
                                        }`}
                                        style={
                                            selectedKPI === option.value
                                                ? { backgroundColor: resolveKpiColor(option.color) }
                                                : {}
                                        }
                                    >
                                        {option.label}
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>{option.fullLabel}</p>
                                </TooltipContent>
                            </Tooltip>
                        ))}
                    </div>
                </nav>
                <div className="basis-full flex justify-center md:basis-auto mt-2 md:mt-0">
                    <label className="flex items-center gap-2 text-xs text-on-surface-variant cursor-pointer select-none">
                        <span>Show trend line</span>
                        <Checkbox
                            checked={showTrendLine}
                            onCheckedChange={(checked) => setShowTrendLine(checked === true)}
                            className="cursor-pointer data-[state=unchecked]:border-on-surface-variant"
                        />
                    </label>
                </div>
            </div>

            <div className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden">
                <ChartContainer
                    config={chartConfig}
                    className="h-full w-full"
                    style={{
                        minWidth: isMobile ? `${Math.max(300, filteredData.length * minWidthPerPoint)}px` : "100%",
                    }}
                >
                    <AreaChart
                        data={filteredData}
                        margin={{
                            top: 30,
                            right: isMobile ? 8 : 30,
                            left: isMobile ? 8 : 30,
                            bottom: isMobile ? 16 : 5,
                        }}
                    >
                        <defs>
                            <linearGradient id={`fill${selectedKPI}`} x1="0" y1="0" x2="0" y2="1">
                                <stop
                                    offset="5%"
                                    stopColor={`var(--color-general-${selectedKPIConfig?.color})`}
                                    stopOpacity={0.8}
                                />
                                <stop
                                    offset="95%"
                                    stopColor={`var(--color-general-${selectedKPIConfig?.color})`}
                                    stopOpacity={0.1}
                                />
                            </linearGradient>
                        </defs>
                        <CartesianGrid vertical={false} stroke="rgba(5, 0, 102, 0.06)" strokeDasharray="3 3" />
                        <XAxis
                            dataKey="date"
                            tickLine={false}
                            axisLine={false}
                            tickMargin={8}
                            tick={
                                isMobile
                                    ? (props: { x?: number; y?: number; index?: number }) => (
                                          <text
                                              x={props.x}
                                              y={(props.y ?? 0) + 12}
                                              fill="#464554"
                                              fontSize={8}
                                              textAnchor="middle"
                                          >
                                              {(props.index ?? 0) + 1}
                                          </text>
                                      )
                                    : filteredData.length > 20
                                      ? false
                                      : { fill: "#464554", fontSize: 10 }
                            }
                            type="category"
                            interval={0}
                            padding={{ left: 20, right: 20 }}
                        />
                        <ChartTooltip
                            cursor={{
                                stroke: `var(--color-general-${selectedKPIConfig?.color})`,
                                strokeWidth: 2,
                                strokeDasharray: "5 5",
                                strokeOpacity: 0.6,
                            }}
                            content={({ active, payload, label }) => {
                                if (!active || !payload?.length) return null;
                                const dataPoint = filteredData.find((item) => item.date === label);
                                if (!dataPoint) return null;
                                const value = dataPoint[selectedKPI];

                                return (
                                    <div className="bg-surface-container-lowest border border-outline-variant/20 text-on-surface rounded-md shadow-[0_8px_32px_rgba(5,0,102,0.12)] p-4 min-w-[220px]">
                                        <div className="flex flex-col gap-2">
                                            <div className="font-semibold text-sm text-general-blue">
                                                Match {dataPoint.matchNumber} - {dataPoint.date}
                                            </div>
                                            <div className="text-xs text-on-surface-variant flex items-center gap-2 flex-wrap">
                                                <span>vs {dataPoint.opponent}</span>
                                                <span
                                                    className={`px-2 py-1 rounded text-xs font-bold ${
                                                        dataPoint.result === "W"
                                                            ? "bg-win-background text-win"
                                                            : dataPoint.result === "D"
                                                              ? "bg-draw-background text-draw"
                                                              : "bg-loss-background text-loss"
                                                    }`}
                                                >
                                                    {dataPoint.result} {dataPoint.score}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2 pt-1 border-t border-outline-variant/20">
                                                <div
                                                    className="w-3 h-3 rounded-full"
                                                    style={{
                                                        backgroundColor: `var(--color-general-${selectedKPIConfig?.color})`,
                                                    }}
                                                />
                                                <span className="text-sm font-medium text-on-surface-variant">
                                                    {selectedKPIConfig?.label}:
                                                </span>
                                                <span
                                                    className="text-lg font-bold"
                                                    style={{
                                                        color: `var(--color-general-${selectedKPIConfig?.color})`,
                                                    }}
                                                >
                                                    {typeof value === "number"
                                                        ? value % 1 === 0
                                                            ? value.toString()
                                                            : value.toFixed(1)
                                                        : value}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }}
                        />
                        <Area
                            dataKey={selectedKPI}
                            type="linear"
                            fill={`url(#fill${selectedKPI})`}
                            stroke={`var(--color-general-${selectedKPIConfig?.color})`}
                            strokeWidth={3}
                            dot={{
                                r: 4,
                                fill: `var(--color-general-${selectedKPIConfig?.color})`,
                                strokeWidth: 0,
                            }}
                            activeDot={{
                                r: 6,
                                fill: `var(--color-general-${selectedKPIConfig?.color})`,
                                strokeWidth: 2,
                                stroke: "white",
                            }}
                        >
                            <LabelList
                                dataKey={selectedKPI}
                                position="top"
                                content={({ value, index, x, y }) => (
                                    <TrendLabel
                                        value={value}
                                        index={index}
                                        x={x}
                                        y={y}
                                        dataKey={selectedKPI}
                                        data={filteredData}
                                    />
                                )}
                            />
                        </Area>
                        {showTrendLine && (
                            <Line
                                dataKey={`${selectedKPI}_trend`}
                                type="linear"
                                stroke="#ba0a06"
                                strokeWidth={3}
                                strokeDasharray="5 5"
                                dot={false}
                                activeDot={false}
                            />
                        )}
                    </AreaChart>
                </ChartContainer>
            </div>
        </div>
    );
}
